import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useBlueprint } from '../../contexts/BlueprintContext';
import { X, Minus, Search, ExternalLink, ChevronRight, Smartphone, BookOpen, GripHorizontal, Layers, ArrowLeft, Lightbulb, AlertTriangle, Link2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';

const BlueprintViewer = () => {
  const {
    isOpen,
    currentBlueprint,
    allBlueprints,
    loading,
    position,
    closeBlueprint,
    updatePosition,
    fetchBlueprintById,
    activeBlockSection,
    setActiveBlockSection,
    getActiveBlockContent,
    showIndicators,
    toggleIndicators,
  } = useBlueprint();

  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' | 'block'
  const viewerRef = useRef(null);

  // Default dimensions
  const defaultWidth = 420;
  const defaultHeight = 560;

  // Calculate centered position
  const getCenteredPosition = useCallback(() => {
    if (typeof window === 'undefined') return { x: 100, y: 100 };
    return {
      x: (window.innerWidth - defaultWidth) / 2,
      y: (window.innerHeight - defaultHeight) / 2
    };
  }, []);

  // Get current position (use stored or calculate center)
  const currentPosition = position.x !== null ? position : getCenteredPosition();

  // Handle drag start
  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    const rect = viewerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Handle drag move
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      updatePosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, updatePosition]);

  // Keyboard shortcut to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (viewMode === 'block') {
          setViewMode('overview');
          setActiveBlockSection(null);
        } else {
          closeBlueprint();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeBlueprint, viewMode, setActiveBlockSection]);

  // Switch to block view when a section is selected
  useEffect(() => {
    if (activeBlockSection) {
      setViewMode('block');
    }
  }, [activeBlockSection]);

  // Filter blueprints for search
  const filteredBlueprints = searchQuery
    ? allBlueprints.filter(b =>
        b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Get the active block content
  const activeBlock = getActiveBlockContent ? getActiveBlockContent() : null;

  // Get the current blueprint
  const bp = currentBlueprint;

  // Get all block sections for navigation
  const blockSections = bp?.content?.blockSections || [];

  if (!isOpen) return null;

  return (
    <div
      ref={viewerRef}
      className="fixed z-[9999] shadow-2xl rounded-lg overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
      style={{
        left: currentPosition.x,
        top: currentPosition.y,
        width: isMinimized ? 300 : defaultWidth,
        height: isMinimized ? 48 : defaultHeight,
        cursor: isDragging ? 'grabbing' : 'default',
        transition: isDragging ? 'none' : 'height 0.2s ease, width 0.2s ease',
      }}
      data-testid="blueprint-viewer"
    >
      {/* Header - Draggable */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-[#70170F] text-white cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          <GripHorizontal className="w-4 h-4 opacity-60" />
          <BookOpen className="w-4 h-4" />
          <span className="font-semibold text-sm">Blueprint</span>
          {bp?.phoneFriendly && (
            <span className="flex items-center gap-1 text-xs bg-white/20 px-1.5 py-0.5 rounded">
              <Smartphone className="w-3 h-3" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 no-drag">
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title="Search blueprints"
          >
            <Search className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title={isMinimized ? "Expand" : "Minimize"}
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={closeBlueprint}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {!isMinimized && (
        <div className="flex flex-col h-[calc(100%-40px)]">
          {/* Search Bar */}
          {showSearch && (
            <div className="p-2 border-b border-gray-200 dark:border-gray-700 no-drag">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search blueprints..."
                className="h-8 text-sm"
                autoFocus
              />
              {searchQuery && filteredBlueprints.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto">
                  {filteredBlueprints.slice(0, 8).map(item => (
                    <button
                      key={item.id}
                      onClick={() => {
                        fetchBlueprintById(item.id);
                        setSearchQuery('');
                        setShowSearch(false);
                      }}
                      className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 rounded flex items-center gap-2"
                    >
                      <span>{item.icon}</span>
                      <span className="flex-1 truncate">{item.title}</span>
                      <span className="text-xs text-gray-500">{item.category}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Context Header */}
          {bp && (
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              {viewMode === 'block' && activeBlock ? (
                // Block Section Header
                <div>
                  <button 
                    onClick={() => { setViewMode('overview'); setActiveBlockSection(null); }}
                    className="flex items-center gap-1 text-xs text-[#70170F] hover:text-[#70170F]/80 mb-1"
                  >
                    <ArrowLeft className="w-3 h-3" />
                    Back to Overview
                  </button>
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-[#70170F]" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">{activeBlock.title}</h3>
                  </div>
                </div>
              ) : (
                // Overview Header
                <>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>{bp.category}</span>
                    {bp.subcategory && (
                      <>
                        <ChevronRight className="w-3 h-3" />
                        <span>{bp.subcategory}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-lg">{bp.icon}</span>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{bp.title}</h3>
                    {bp.phoneFriendly && (
                      <span className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                        <Smartphone className="w-3 h-3" />
                        Phone Friendly
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Block Sections Navigation Bar */}
          {bp && blockSections.length > 0 && viewMode === 'overview' && (
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-[#70170F]/5">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-[#70170F]" />
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Page Sections</span>
                <Badge variant="secondary" className="text-[10px]">{blockSections.length}</Badge>
              </div>
              <div className="flex flex-wrap gap-1">
                {blockSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => { setActiveBlockSection(section.id); setViewMode('block'); }}
                    className="text-xs px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-[#70170F] hover:text-[#70170F] transition-colors"
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Scrollable Content */}
          <ScrollArea className="flex-1 no-drag">
            <div className="p-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[#70170F] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : bp ? (
                viewMode === 'block' && activeBlock ? (
                  // Block Section Content
                  <div className="space-y-4">
                    {/* Description */}
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {activeBlock.description}
                    </p>

                    {/* How to Use */}
                    {activeBlock.howToUse?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                          How to Use
                        </h4>
                        <div className="space-y-2">
                          {activeBlock.howToUse.map((step, i) => (
                            <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#70170F] text-white text-xs flex items-center justify-center">
                                {i + 1}
                              </span>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tips */}
                    {activeBlock.tips?.length > 0 && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Lightbulb className="w-4 h-4 text-amber-600" />
                          <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">Tips</span>
                        </div>
                        <ul className="space-y-1">
                          {activeBlock.tips.map((tip, i) => (
                            <li key={i} className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
                              <span>•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Warnings */}
                    {activeBlock.warnings?.length > 0 && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-xs font-semibold text-red-800 dark:text-red-300">Important</span>
                        </div>
                        <ul className="space-y-1">
                          {activeBlock.warnings.map((warning, i) => (
                            <li key={i} className="text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
                              <span>•</span>
                              <span>{warning}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Related Features */}
                    {activeBlock.relatedFeatures?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Link2 className="w-4 h-4 text-gray-500" />
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Related Features</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {activeBlock.relatedFeatures.map((feature, i) => (
                            <Badge key={i} variant="outline" className="text-xs cursor-pointer hover:bg-[#70170F]/10">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Overview Content (original)
                  <>
                    {/* Overview */}
                    {bp.content?.overview && (
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          {bp.content.overview}
                        </p>
                      </div>
                    )}

                    {/* Quick Start */}
                    {bp.content?.quickStart?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                          Quick Start
                        </h4>
                        <div className="space-y-2">
                          {bp.content.quickStart.map((step, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                            >
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#70170F] text-white text-xs flex items-center justify-center">
                                {step.step}
                              </span>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{step.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sections */}
                    {bp.content?.sections?.map((section, i) => (
                      <div key={i}>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                          {section.title}
                        </h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                          {section.content}
                        </p>
                        {section.tips?.length > 0 && (
                          <div className="space-y-1 ml-2">
                            {section.tips.map((tip, j) => (
                              <div key={j} className="flex items-start gap-2 text-sm">
                                <span className="text-amber-500">💡</span>
                                <span className="text-gray-600 dark:text-gray-400">{tip}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Pro Tips */}
                    {bp.content?.proTips?.length > 0 && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                        <h4 className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider mb-2">
                          Pro Tips
                        </h4>
                        <ul className="space-y-1">
                          {bp.content.proTips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-200">
                              <span>›</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Related Blueprints */}
                    {bp.content?.related?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                          Related Blueprints
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {bp.content.related.map((relatedId) => {
                            const related = allBlueprints.find(b => b.id === relatedId);
                            if (!related) return null;
                            return (
                              <button
                                key={relatedId}
                                onClick={() => fetchBlueprintById(relatedId)}
                                className="flex items-center gap-1.5 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                              >
                                <span>{related.icon}</span>
                                <span>{related.title}</span>
                                <ChevronRight className="w-3 h-3" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No Blueprint available for this page.</p>
                  <p className="text-xs mt-1">Try searching for a topic above.</p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500 flex items-center justify-between no-drag">
            <span>Press Esc to close</span>
            <a 
              href="/blueprints" 
              className="flex items-center gap-1 text-[#70170F] hover:underline"
            >
              View all Blueprints
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlueprintViewer;
